// RW-440 — collectionGroup('tasks') is owner/role-gated in firestore.rules.
//
// The CG read rule is:
//   allow read: if isAdminOrTC()
//     || resource.data.createdBy == request.auth.uid
//     || resource.data.assignedTo.userId == request.auth.uid;
//
// Per "rules are not filters", a non-admin/TC user's collectionGroup('tasks')
// query is permission-denied UNLESS it carries a where() clause matching one of
// the owner predicates. So agents must run TWO owner-scoped queries
// (where('createdBy','==',uid) and where('assignedTo.userId','==',uid)) and
// merge them client-side; admin/TC may run the unfiltered tenant-scoped query.
//
// Prod task ownership is exactly { createdBy, assignedTo:{userId} | null }
// (verified 2026-06-20 across the live tasks collection group — no legacy
// string/array assignedTo shapes), so this union reproduces the historical
// ownedByUser definition without missing any tasks.
//
// These helpers centralize the role branch + dual-query merge so every CG-tasks
// surface (Dashboard, MyTasks, Calendar, FocusMode, sidebar overdue badge)
// stays in lock-step with the rule.

import { collectionGroup, query, where, limit as fbLimit, onSnapshot, getDocs } from 'firebase/firestore';
import { isAdminOrTC } from './permissions';
import { logErrorToFirestore } from '../components/ErrorBoundary';

const mapDoc = (d) => ({ id: d.id, path: d.ref.path, ...d.data() });

// A failed CG-tasks query previously ended at console.warn — most callers pass
// no onError, so a broken index (RW-1462: assignedTo.userId/createdBy had no
// COLLECTION_GROUP index) silently emptied every task surface for non-admins
// and nothing auto-ticketed. File it to errorLogs instead (RW-1661 pattern);
// one report per label/code per session, error codes only, no task contents.
const reportedQueryFailures = new Set();
function reportTaskQueryFailure(label, err) {
  const code = err?.code || err?.name || 'unknown';
  const key = `${label}/${code}`;
  if (reportedQueryFailures.has(key)) return;
  reportedQueryFailures.add(key);
  logErrorToFirestore({
    type: 'task-query-failed',
    message: `[${label}] tasks CG query failed (${code})`,
    code: String(code),
    stack: (err?.message || '').slice(0, 500),
  });
}

const dedupeByPath = (...lists) => {
  const byPath = new Map();
  for (const list of lists) for (const t of list) byPath.set(t.path, t);
  return Array.from(byPath.values());
};

// Subscribe to the caller's own tasks (createdBy OR assignedTo.userId == uid),
// merged + de-duped. Works for any role. Returns an unsubscribe function.
function subscribeOwnedUnion(db, uid, max, onResult, handleErr) {
  if (!uid) { onResult([]); return () => {}; }
  let created = [];
  let assigned = [];
  const emit = () => onResult(dedupeByPath(created, assigned));
  const uCreated = onSnapshot(
    query(collectionGroup(db, 'tasks'), where('createdBy', '==', uid), fbLimit(max)),
    (snap) => { created = snap.docs.map(mapDoc); emit(); },
    handleErr,
  );
  const uAssigned = onSnapshot(
    query(collectionGroup(db, 'tasks'), where('assignedTo.userId', '==', uid), fbLimit(max)),
    (snap) => { assigned = snap.docs.map(mapDoc); emit(); },
    handleErr,
  );
  return () => { uCreated(); uAssigned(); };
}

/**
 * Role-branched task subscription. Admin/TC get the unfiltered tenant-scoped CG
 * query; everyone else gets their owner-scoped union. For surfaces that show a
 * tenant-wide task view to staff (Dashboard, MyTasks, Calendar).
 *
 * @returns {() => void} unsubscribe
 */
export function subscribeTasks(db, { user, tenantId, max = 500, onResult, onError }) {
  const handleErr = (err) => { console.warn('tasks CG query failed:', err?.message); reportTaskQueryFailure('tasks', err); if (onError) onError(err); };
  if (isAdminOrTC(user)) {
    if (!tenantId) { onResult([]); return () => {}; }
    return onSnapshot(
      query(collectionGroup(db, 'tasks'), where('tenantId', '==', tenantId), fbLimit(max)),
      (snap) => onResult(snap.docs.map(mapDoc)),
      handleErr,
    );
  }
  return subscribeOwnedUnion(db, user && user.uid, max, onResult, handleErr);
}

/**
 * Always-owner-scoped subscription (ignores role). For surfaces that only ever
 * show the current user's own tasks regardless of role — the sidebar overdue
 * badge. Returns an unsubscribe function.
 */
export function subscribeOwnedTasks(db, { user, max = 500, onResult, onError }) {
  const handleErr = (err) => { console.warn('owned-tasks CG query failed:', err?.message); reportTaskQueryFailure('owned-tasks', err); if (onError) onError(err); };
  return subscribeOwnedUnion(db, user && user.uid, max, onResult, handleErr);
}

/**
 * One-shot (getDocs) owner-scoped fetch — the current user's own tasks, merged
 * + de-duped. For non-realtime aggregators (FocusMode). Returns Promise<Task[]>.
 */
export async function fetchOwnedTasks(db, { user, max = 500 }) {
  const uid = user && user.uid;
  if (!uid) return [];
  const [c, a] = await Promise.all([
    getDocs(query(collectionGroup(db, 'tasks'), where('createdBy', '==', uid), fbLimit(max))),
    getDocs(query(collectionGroup(db, 'tasks'), where('assignedTo.userId', '==', uid), fbLimit(max))),
  ]);
  return dedupeByPath(c.docs.map(mapDoc), a.docs.map(mapDoc));
}
